import React from "react";
import { createContext, useReducer, useContext } from "react";
import { v4 as uuidv4 } from "uuid";

import * as ACTION from "./action";

import { ErrorModal } from "../../component/common/modal/error-modal";
import { WarningModal } from "../../component/common/modal/warning-modal";
import { WaitingModal } from "../../component/common/modal/waiting-modal";
import { ConfirmModal } from "../../component/common/modal/confirm-modal";
import { QuestionModal } from "../../component/common/modal/question-modal";
import { SuccessToast } from "./component/success-toast";

const UiFeedbackContext = createContext();

const createSuccessToast = (msg) => {
  const uuid = uuidv4();
  return {
    uuid: uuid,
    toast: <SuccessToast uuid={uuid} msg={msg} />,
  };
};

const uiFeedbackReducer = (state, action) => {
  switch (action.type) {
    case ACTION.ERROR: {
      return {
        ...state,
        error: action.error,
      };
    }
    case ACTION.WARNING: {
      return {
        ...state,
        warning: action.warning,
      };
    }
    case ACTION.WAITING: {
      return {
        ...state,
        waiting: action.waiting,
      };
    }
    case ACTION.CONFIRM: {
      return {
        ...state,
        confirm: action.confirm,
      };
    }
    case ACTION.QUESTION: {
      return {
        ...state,
        question: action.question,
      };
    }
    case ACTION.SUCCESS: {
      return {
        ...state,
        success: [createSuccessToast(action.success), ...state.success],
      };
    }
    case ACTION.CLOSE_SUCCESS: {
      return {
        ...state,
        success: [...state.success.filter((s) => s.uuid !== action.uuid)],
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

export const UiFeedbackProvider = ({ children }) => {
  const [state, uiFeedbackDispatch] = useReducer(uiFeedbackReducer, {
    error: null,
    warning: null,
    waiting: null,
    confirm: null,
    question: null,
    success: [],
  });
  const value = { state, uiFeedbackDispatch };

  return (
    <UiFeedbackContext.Provider value={value}>
      <>
        {children}
        {state.error ? (
          <ErrorModal
            title={state.error.title}
            text={state.error.msg}
            handleClose={() => {
              uiFeedbackDispatch({
                type: ACTION.ERROR,
                error: null,
              });
              state.error.action();
            }}
          />
        ) : null}
        {state.warning ? (
          <WarningModal
            title={state.warning.title}
            text={state.warning.msg}
            handleClose={() => {
              uiFeedbackDispatch({
                type: ACTION.WARNING,
                warning: null,
              });
              state.warning.action();
            }}
          />
        ) : null}
        {state.confirm ? (
          <ConfirmModal
            title={state.confirm.title}
            text={state.confirm.text}
            handleOk={() => {
              uiFeedbackDispatch({
                type: ACTION.CONFIRM,
                confirm: null,
              });
              state.confirm.handleOk();
            }}
            handleClose={() => {
              uiFeedbackDispatch({
                type: ACTION.CONFIRM,
                confirm: null,
              });
              state.confirm.handleClose();
            }}
          />
        ) : null}
        {state.question ? (
          <QuestionModal
            title={state.question.title}
            question={state.question.question}
            handleOk={() => {
              uiFeedbackDispatch({
                type: ACTION.QUESTION,
                question: null,
              });
              state.question.handleOk();
            }}
            handleClose={() => {
              uiFeedbackDispatch({
                type: ACTION.QUESTION,
                question: null,
              });
              state.question.handleClose();
            }}
          >
            {state.question.children}
          </QuestionModal>
        ) : null}
        {state.waiting ? <WaitingModal /> : null}
        {state.success.length > 0 ? (
          <div style={{ position: "fixed", bottom: 0, right: 15 }}>
            {state.success.map((t) => {
              return <div key={t.uuid}>{t.toast}</div>;
            })}
          </div>
        ) : null}
      </>
    </UiFeedbackContext.Provider>
  );
};

export const useUiFeedback = () => {
  const context = useContext(UiFeedbackContext);
  if (context === undefined) {
    throw new Error("useUiFeedback must be used within a UiFeedbackProvider");
  }
  return context;
};
